import type React from "react";
import { useTranslation } from "react-i18next";
// コンポーネント
import DataVisibilityToggle from "./DataVisibilityToggle";
// カスタムフック
import { useTableColumns } from "./hooks/useTableColumns";
import TableHeader from "./TableHeader";
import TableRow, { RowData } from "./TableRow";

interface Props {
  data: RowData[];
  latestYM: Date;
  selectedCategoryPath?: string;
  // 展開状態の行を保持しておくstate
  expandedRows: Set<string>;
  setExpandedRows: (expandedRows: Set<string>) => void;
  // テーブルに何ヶ月表示するか (デフォルトは2ヶ月)、年次の場合は年数
  displayMonths?: number;
  // 年次表示フラグ
  isYearlyView?: boolean;
  // 「データを開く/閉じる」ボタンを非表示にするかどうか
  hideDataVisibilityToggle?: boolean;
  // 「データを開く/閉じる」ボタンを表示しているときの開閉状態
  isHidingWithFog: boolean;
  setIsHidingWithFog: (isHidingWithFog: boolean) => void;
  // 「データを開く/閉じる」ボタンを表示かつ閉じている状態のときの表示行数制限
  initialRowLimit?: number;
  // テーブルの行をクリックしたときのhandler
  onRowClick: (row: RowData) => void;
  // テーブルの各行の分析ボタンをクリックしたときのhandler
  onLinkClick: (row: RowData) => void;
}

/**
 * BIテーブルコンポーネント
 * VOCデータを階層構造で表示するテーブル
 */
const BITable: React.FC<Props> = ({
  data,
  latestYM,
  selectedCategoryPath,
  isHidingWithFog,
  setIsHidingWithFog,
  expandedRows,
  setExpandedRows,
  displayMonths = 2,
  isYearlyView,
  initialRowLimit = 2,
  hideDataVisibilityToggle = false,
  onRowClick,
  onLinkClick,
}) => {
  const { t } = useTranslation("voiceek_ui");

  const columns = useTableColumns(latestYM, displayMonths, t, isYearlyView);

  // 行の展開/折りたたみ状態を管理する関数
  const isExpanded = (rowId: string) => expandedRows.has(rowId);
  const toggleRow = (rowId: string) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(rowId)) {
      newExpandedRows.delete(rowId);
    } else {
      newExpandedRows.add(rowId);
    }
    setExpandedRows(newExpandedRows);
  };

  // 再帰的に行をレンダリングする関数
  const renderRowsRecursively = (rows: RowData[], renderedRows: React.ReactNode[], level = 0): void => {
    rows.forEach((row, index) => {
      // 行数制限の適用
      if (!hideDataVisibilityToggle && isHidingWithFog && index >= initialRowLimit && level === 0) return;

      // 行の追加
      renderedRows.push(
        <TableRow
          key={row.categoryPath}
          row={row}
          columns={columns}
          level={level}
          displayMonths={displayMonths}
          isSelected={row.categoryPath === selectedCategoryPath}
          isExpanded={isExpanded(row.categoryPath)}
          onRowClick={(row: RowData) => isHidingWithFog ? setIsHidingWithFog(false) : onRowClick(row)}
          onLinkClick={(row: RowData) => isHidingWithFog ? setIsHidingWithFog(false) : onLinkClick(row)}
          onToggleExpand={(rowId: string) => isHidingWithFog ? setIsHidingWithFog(false) : toggleRow(rowId)}
        />,
      );

      // 子行の再帰的レンダリング
      if (row.children && row.children.length > 0 && isExpanded(row.categoryPath)) {
        renderRowsRecursively(row.children, renderedRows, level + 1);
      }
    });
  };

  /**
   * テーブルの行を再帰的にレンダリングする
   */
  const renderTableContent = () => {
    const renderedRows: React.ReactNode[] = [];
    renderRowsRecursively(data, renderedRows);
    return renderedRows;
  };

  // データを開く/閉じるボタンのクリックハンドラ
  const handleClickVisibilityToggle = () => {
    // データを閉じるを押したときは、展開状態もリセットして初期状態に戻す
    if (!isHidingWithFog) setExpandedRows(new Set([]));

    setIsHidingWithFog(!isHidingWithFog);
  };

  return (
    <div className="w-full overflow-auto">
      <table className="w-full border-collapse">
        <TableHeader columns={columns} />
        <tbody className="relative z-0">
          {renderTableContent()}

          {!hideDataVisibilityToggle && (
            <DataVisibilityToggle
              onClick={handleClickVisibilityToggle}
              columnsCount={columns.length}
              isHiding={isHidingWithFog}
            />
          )}
        </tbody>
      </table>
    </div>
  );
};

export default BITable;
